import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import moment from "moment-timezone";
import { BriefcaseBusiness, CalendarCheck2, CalendarDays, Clock, HelpCircle } from "lucide-react";
import Dialog from "../../../../../../../components/shared/Dialog";
import Button from "../../../../../../../components/shared/Button";
import Avatar from "../../../../../../../components/shared/Avatar";
import BaseForm, { Field } from "../../../../../../../components/shared/Form";
import { Textarea } from "../../../../../../../components/ui/textarea";
import { useVisible } from "../../../../../../../utils/hooks/useVisible";
import { useCompanyTimeFormat } from "../../../../../../../utils/hooks/company";
import useApi from "../../../../../../../utils/api";
import { getErrorMessage } from "../../../../../../../utils/helpers/errors";
import { renderUserName } from "../../../../../../../utils/helpers/user";
import { renderSettingsResourceLabel } from "../../../../../../../utils/helpers/settings";
import { withLoadingToast } from "../../../../helpers/withLoadingToast";
import { useScheduleContext } from "../../../../hooks/context/useScheduleContext";
import { getShiftInfo } from "./helpers";
import { OPEN_SHIFTS_URL } from "./constants";

type ClaimFormValues = {
    approveReason: string;
};

export const useClaimDialog = () => {
    const { t } = useTranslation();
    const timeFormat = useCompanyTimeFormat();
    const { authPut } = useApi();
    const queryClient = useQueryClient();
    const { dispatch } = useScheduleContext();
    const { open, close, visible, selected } = useVisible();

    const form = useForm<ClaimFormValues>({
        defaultValues: { approveReason: "" },
    });

    const onClose = useCallback(() => {
        form.reset();
        close();
    }, [close, form]);

    const { mutate: claim, isPending } = useMutation({
        ...withLoadingToast(t("claiming")),
        mutationFn: ({ request, approveReason }: { request: any; approveReason: string }) => (
            authPut(`/request/${request.id}/approve`, approveReason && { data: { approveReason } })
        ),
        onSuccess: () => {
            toast.success(t("shift-claimed"));
            queryClient.invalidateQueries({ queryKey: [OPEN_SHIFTS_URL] });
            dispatch({ type: "refetch-events" });
            onClose();
        },
        onError: (error) => {
            getErrorMessage(error, t);
            onClose();
        },
    });

    const onFinish = useCallback((values: ClaimFormValues) => {
        claim({ request: selected, approveReason: values.approveReason });
    }, [claim, selected]);

    const dialog = useMemo(() => {
        const { owner, date, startDate, endDate, job, location } = getShiftInfo(selected);

        return (
            <Dialog open={visible} close={onClose} size="lg">
                <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-mb-5">
                    <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-primary/10 tw-flex tw-items-center tw-justify-center tw-mb-3">
                        <HelpCircle size={22} className="tw-text-primary" />
                    </div>
                    <h2 className="tw-text-sm tw-font-semibold">{t("do-you-want-to-claim-this-shift")}</h2>
                </div>

                {selected && (
                    <div className="tw-border tw-rounded-lg tw-p-3 tw-mb-5 tw-flex tw-flex-col tw-gap-2">
                        {owner && (
                            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                                <Avatar user={owner} size="sm" />
                                <span>{renderUserName(owner)}</span>
                            </div>
                        )}
                        <div className="tw-flex tw-items-center tw-gap-3 tw-text-sm">
                            <CalendarDays size={17} className="tw-text-primary" />
                            {moment(date).format("ddd, DD MMMM YYYY")}
                        </div>
                        {startDate && endDate && (
                            <div className="tw-flex tw-items-center tw-gap-3 tw-text-sm">
                                <Clock size={17} className="tw-text-primary" />
                                {moment.parseZone(startDate).format(timeFormat)}
                                {" - "}
                                {moment.parseZone(endDate).format(timeFormat)}
                            </div>
                        )}
                        {(job || location) && (
                            <div className="tw-flex tw-items-center tw-gap-3 tw-text-sm">
                                <BriefcaseBusiness size={17} className="tw-text-primary" />
                                {[job, location].filter(Boolean).map(renderSettingsResourceLabel).join(" · ")}
                            </div>
                        )}
                    </div>
                )}

                <BaseForm form={form} onFinish={onFinish}>
                    <Field name="approveReason" label={t("reason")}>
                        <Textarea rows={3} className="tw-resize-none field-sizing-fixed" placeholder={t("reason")} />
                    </Field>

                    <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2">
                        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                            {t("cancel")}
                        </Button>
                        <Button
                            type="submit"
                            icon={<CalendarCheck2 />}
                            loading={isPending}
                            disabled={isPending}
                        >
                            {t("claim")}
                        </Button>
                    </div>
                </BaseForm>
            </Dialog>
        );
    }, [t, timeFormat, visible, onClose, selected, isPending, form, onFinish]);

    return { open, dialog };
};
